import { useEffect, useRef, useState } from 'react';
import API from '../../api/api';
import { MediaItem } from '../../api/structs/media';
import { isPlaceholder } from '../clips/sections/MediaEditorSection.helpers';
import { FrameBinding, resolveFrameBindingItem } from '../clips/sections/referenceFrames';
import { readContentVersion } from '../../api/mediaUrls';

/** What a frame's bytes looked like when the modal opened. */
export interface FrameSnapshot {
  id: string;
  fileUrl: string;
  contentVersion?: number;
}

export const snapshotOf = (item: MediaItem | undefined): FrameSnapshot | null =>
  item
    ? { id: item.id, fileUrl: item.file_url, contentVersion: readContentVersion(item.metadata) }
    : null;

/**
 * A frame regeneration landed since the snapshot was taken. `file_url` moves on
 * every RegenerateIntoVariant (the old bytes are preserved as a take at the old
 * URL), `content_version` is the belt-and-braces for any in-place rewrite that
 * reuses the URL. Waiting/failed placeholders are never "a new frame".
 */
export const hasNewFrame = (
  snapshot: FrameSnapshot | null,
  live: MediaItem | undefined
): boolean => {
  if (!snapshot || !live?.file_url) return false;
  if (isPlaceholder(live.file_url)) return false;
  return (
    live.file_url !== snapshot.fileUrl ||
    readContentVersion(live.metadata) !== snapshot.contentVersion
  );
};

/**
 * Floor between two getMediaItem reads for the same frame binding. The load
 * effect below is deliberately keyed on `allItems` IDENTITY (a clip refetch is
 * the only signal that a run-owned frame changed), but identity is minted by
 * plain re-renders too — an upstream component that rebuilds its media arrays
 * per render turned this into an unbounded request loop (two frames firing
 * alternately until the tab froze, 2026-09-01). Leading edge stays instant so
 * the refresh handshake arms immediately after a quiet signal; churn coalesces
 * into one trailing read per interval instead of one per render.
 */
const FRAME_REFETCH_MIN_INTERVAL_MS = 2000;

/** What we could reach behind ONE of the video's frame bindings. */
export interface ResolvedFrame {
  /**
   * The item this slot's form regenerates. The binding's own item when we can
   * reach one, otherwise the scene's still — see `bound` for the difference.
   */
  item?: MediaItem;
  /**
   * The item the BINDING actually names, and nothing else. Undefined for a slot
   * whose binding reached nothing: the frame is then a bare URL, `item` may
   * still hold the clip's still as a regeneration target, but every claim about
   * what the VIDEO is made from has to be made off this one.
   */
  bound?: MediaItem;
  /**
   * The frame's bytes the FIRST time we saw it — the handshake baseline. Taken
   * off `bound` for a slot that HAS a binding, so it always describes the frame
   * itself; off `item` only for a slot nothing binds, where the scene's own
   * still is the frame.
   */
  baseline: FrameSnapshot | null;
  /** A `getMediaItem` lookup for this binding is still in flight. */
  loading: boolean;
}

/**
 * Resolve a frame binding to the media item behind it.
 *
 * `scene.allItems` only ever holds the CLIP's media, and that is enough for a
 * reel that emits stills: its frames ARE clip image items, so they match by id
 * or by URL without leaving the page. A VIDEO-ONLY reel (a final-assembly
 * template emitting `imagePrompts: []` — which is how the user's reels are
 * built on purpose) has no image bucket at all. Its frames were generated in
 * the PIPELINE RUN and exist only as reference URLs on the video, so the scene
 * pool can never resolve them and the binding's media id is the sole handle
 * back to the image — hence the fetch.
 *
 * Reaching nothing is a normal outcome, not an error: pre-existing clips carry
 * a closing frame with no id at all (it was URL-only by construction until
 * assembly.go started stamping `last_reference_media_id`), and an id can 404
 * after the run's media is swept. Every failure path lands on `item:
 * undefined`, which the caller renders as the honest view/swap-only frame.
 */
export const useResolvedFrame = (
  binding: FrameBinding | null,
  allItems: MediaItem[],
  fallback?: MediaItem
): ResolvedFrame => {
  // THE BINDING WINS. `fallback` is the scene's own still, and it never stands
  // in for a binding that DID resolve: preferring it outright is what quietly
  // made this dialog show — and regenerate — the clip's still whenever the
  // video's opening frame had been swapped to some other image, so the pane
  // claimed to be about the frames the video is made from and was about
  // something else.
  //
  // It is still consulted twice, at the bottom: for a slot the video does not
  // bind at all (an image-only scene), and as a REGENERATION TARGET for a
  // legacy URL-only binding that reached nothing. The second case does not make
  // the slot resolved — `bound` stays undefined and the caller still lists it
  // as unresolved — it only means the operator keeps the still's regenerate
  // form instead of losing it.
  //
  // WHICH item the binding named is latched on first sight and followed BY ID
  // from then on. Re-matching every render would lose the item the moment its
  // file_url moves — and a moved file_url is exactly the event this dialog
  // exists to notice (it is what arms "Use new closing frame").
  const identified = useRef<string | null>(null);
  const firstMatch = binding ? resolveFrameBindingItem(binding, allItems) : undefined;
  if (!identified.current && firstMatch) identified.current = firstMatch.id;
  const local = identified.current
    ? allItems.find((entry) => entry.id === identified.current) ?? firstMatch
    : firstMatch;
  const mediaId = binding?.mediaId?.trim() ?? '';
  const needsFetch = !local && mediaId !== '';

  const [fetched, setFetched] = useState<MediaItem | undefined>(undefined);
  const [loading, setLoading] = useState(needsFetch);
  const inFlight = useRef(false);
  const lastFetchAt = useRef(0);

  useEffect(() => {
    if (!needsFetch) {
      setFetched(undefined);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const run = () => {
      inFlight.current = true;
      lastFetchAt.current = Date.now();
      setLoading(true);
      // Wrapped in a resolved promise so a synchronous throw (a missing client,
      // a bad id) lands in the same catch as a rejected request — this lookup is
      // an enhancement, and nothing about it may take the dialog down.
      Promise.resolve()
        .then(() => API.getMediaItem(mediaId))
        .then((item: MediaItem | undefined) => {
          if (cancelled) return;
          // A 200 with a body we cannot use is the same as a miss.
          setFetched(item && item.id ? item : undefined);
        })
        .catch(() => {
          if (!cancelled) setFetched(undefined);
        })
        .finally(() => {
          inFlight.current = false;
          if (!cancelled) setLoading(false);
        });
    };

    // Throttle, do not skip: every effect firing still leads to a read, but
    // rapid-fire firings collapse into ONE trailing read. Skipping instead of
    // trailing would drop the one genuine refresh hidden inside a churn burst
    // and the "Use new … frame" handshake could never arm.
    const sinceLast = Date.now() - lastFetchAt.current;
    if (!inFlight.current && sinceLast >= FRAME_REFETCH_MIN_INTERVAL_MS) {
      run();
      return () => {
        cancelled = true;
      };
    }
    const timer = window.setTimeout(() => {
      if (!cancelled && !inFlight.current) run();
    }, Math.max(FRAME_REFETCH_MIN_INTERVAL_MS - sinceLast, 50));
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // `allItems` is a dependency ON PURPOSE. Its identity changes only when the
    // clip is refetched (MediaEditorSection rebuilds the scene groups from new
    // media props on a WS event), and for a frame that lives on the pipeline
    // run that refetch is the ONLY signal this modal ever gets that a
    // regeneration landed: the frame is not in the clip, so the live-prop drift
    // that arms the "Use new … frame" handshake for clip stills cannot happen
    // and `settleRegenerateWatches` drops its watch immediately. Re-reading the
    // item here is what replaces both.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFetch, mediaId, allItems]);

  const bound = local ?? fetched;
  // The rescue: a slot that reached nothing still offers the scene's own still
  // to regenerate, which is what a legacy clip (closing URL stamped before
  // `last_reference_media_id` existed) has. Never while the lookup is still in
  // flight — the fetched item is the better answer and is about to arrive.
  const item = bound ?? (loading ? undefined : fallback);
  // First-sight latch. The baseline cannot be taken at open like the clip-still
  // one is — a fetched frame simply is not there yet on the first render — but
  // it must still be taken exactly ONCE, or every refetch would reset the
  // comparison and the handshake could never fire.
  //
  // It is latched off `bound`, NOT `item`, whenever the slot has a binding: a
  // lookup that fails once drops `item` onto the rescue still, and latching
  // there meant the retry's success — the very same frame, freshly resolved —
  // compared unequal and the dialog announced a regeneration that never
  // happened. A slot nothing binds has no `bound` to wait for, and its still is
  // the frame, so it latches off `item`.
  const baseline = useRef<FrameSnapshot | null>(null);
  const baselineSubject = binding ? bound : item;
  if (!baseline.current && baselineSubject) baseline.current = snapshotOf(baselineSubject);

  return { item, bound, baseline: baseline.current, loading };
};
